import { Redirect } from 'react-router-dom';
import type { match } from 'react-router';
import urlJoin from 'url-join';
import type { MessageTranslator } from '@oneflowab/pomes';
import type { ReactNode } from 'react';

import adminPage from '@app/hocs/admin-page';
import type { PropsMapper } from '@app/hocs/admin-page';

import { checkAcl } from '@flow/libs/utils/acl';
import { NotFound } from '@app/components/errors';

import TemplateGroups from './template-groups';
import ApiTokens from './api-tokens';
import Webhooks from './webhooks';

type Props = {
  match: match;
  acl: {
    account: Oneflow.Account['acl'];
    workspace: Oneflow.Workspace['acl'];
  };
};

export const WorkflowsPage = (props: Props) => {
  const { acl } = props;

  if (checkAcl(acl.account, 'account:admin:workflows:template_groups')) {
    return (
      <Redirect
        from={props.match.path}
        to={urlJoin(props.match.url, '/template-groups')}
      />
    );
  }

  if (checkAcl(acl.account, 'account:admin:workflows:api_tokens')) {
    return (
      <Redirect
        from={props.match.path}
        to={urlJoin(props.match.url, '/api-tokens')}
      />
    );
  }

  if (checkAcl(acl.account, 'account:admin:workflows:webhooks')) {
    return (
      <Redirect
        from={props.match.path}
        to={urlJoin(props.match.url, '/webhooks')}
      />
    );
  }

  return (
    <NotFound
      {...props}
      redirect
      to="/dashboard"
    />
  );
};

type Module = {
  component: typeof TemplateGroups | typeof ApiTokens | typeof Webhooks;
  hideBreadCrumb: boolean;
  path: string;
  section: ReactNode;
  title: ReactNode;
};

type MapperProps = {
  message: (args: { id: string; comment?: string }) => string;
  acl: {
    account: Oneflow.Account['acl'];
    workspace: Oneflow.Workspace['acl'];
  };
};

const getWorkflowsModules = ({
  acl,
  message,
}: {
  acl: Oneflow.Account['acl'];
  message: MessageTranslator;
}) => {
  let modules: Module[] = [];

  if (checkAcl(acl, 'account:admin:workflows:template_groups')) {
    modules = [
      ...modules,
      {
        component: TemplateGroups,
        hideBreadCrumb: true,
        path: '/template-groups',
        section: message({
          id: 'Workflows',
          comment: 'Used as the title for the section.',
        }),
        title: message({
          id: 'Template groups',
          comment: 'Page title for the workflows page.',
        }),
      },
    ];
  }

  if (checkAcl(acl, 'account:admin:workflows:api_tokens')) {
    modules = [
      ...modules,
      {
        component: ApiTokens,
        hideBreadCrumb: true,
        path: '/api-tokens',
        section: message({
          id: 'Workflows',
          comment: 'Used as the title for the section.',
        }),
        title: message({
          id: 'API tokens',
          comment: 'Page title for the workflows page.',
        }),
      },
    ];
  }

  if (checkAcl(acl, 'account:admin:workflows:webhooks')) {
    modules = [
      ...modules,
      {
        component: Webhooks,
        hideBreadCrumb: true,
        path: '/webhooks',
        section: message({
          id: 'Workflows',
          comment: 'Used as the title for the section.',
        }),
        title: message({
          id: 'Webhooks',
          comment: 'Page title for the workflows page.',
        }),
      },
    ];
  }

  return modules;
};

export const propsMapper: PropsMapper = ({ props }: { props: MapperProps }) => {
  return {
    title: props.message({
      id: 'Workflows',
      comment: 'The page title',
    }),
    showAsLink: true,
    modules: [
      getWorkflowsModules({
        acl: props.acl.account,
        message: props.message,
      }),
    ],
  };
};

export default adminPage(propsMapper)(WorkflowsPage);
